import { cn } from '@foundation/utils/cn';
import { createContext, ReactNode, useContext } from 'react';

import { Close, CheckCircle } from '@design-system/icons';

export type ChecklistProps = {
  readonly children: ReactNode;
};

const ChecklistContext = createContext(false);

function Root({ children }: ChecklistProps) {
  return (
    <ChecklistContext.Provider value={true}>
      <ul className="flex flex-col gap-2">{children}</ul>
    </ChecklistContext.Provider>
  );
}

export type ChecklistItemStatus = 'included' | 'excluded';

export type ChecklistItemProps = {
  readonly status?: ChecklistItemStatus;
  readonly children: ReactNode;
};

const STATUS_CLASSNAMES = {
  included: 'text-pmui-fg-status-positive-default',
  excluded: 'text-pmui-fg-status-negative-default',
} as const satisfies Record<ChecklistItemStatus, string>;

function Item({ status = 'included', children }: ChecklistItemProps) {
  const isInsideChecklist = useContext(ChecklistContext);
  if (!isInsideChecklist) {
    throw new Error('Checklist.Item must be used within Checklist');
  }

  const Icon = status === 'included' ? CheckCircle : Close;

  return (
    <li
      className={cn(
        'flex items-start gap-2 font-body font-emphasis text-sm leading-5 tracking-tightest',
        STATUS_CLASSNAMES[status],
      )}
    >
      <Icon
        size={16}
        className="mt-0.5 shrink-0"
      />
      <span className={cn(status === 'excluded' && 'line-through')}>
        {children}
      </span>
    </li>
  );
}

export const Checklist = Object.assign(Root, { Item });
